import {
  TRIP_CHAT_STREAM_EVENT,
  type paths,
  type definitions,
  type TripChatCardsListUiComponentData,
  type TripChatCarouselUiComponentData,
  type TripChatProgressiveUiComponentData,
  type TripChatProgressiveUiPatchOp,
  type TripChatPillsListUiComponentData,
  type TripChatTestimonialUiComponentData,
  type TripChatUiPartFailedEvent,
  type TripChatUiPartPatchEvent,
  type TripChatEnrichmentCompleteEvent,
} from '@luxuryescapes/contract-trip'
import type { TypeaheadTravelDates } from '@luxuryescapes/contract-search'

export type GetTripsResponse =
  paths['/api/trips']['get']['responses']['200']['content']['application/json']

export type GetTripCollaboratorsResponse =
  paths['/api/trips/{tripId}/collaborators']['get']['responses']['200']['content']['application/json']

export type CreateTripResponse =
  paths['/api/trips']['post']['responses']['200']['content']['application/json']

export type CreateTripRequest =
  paths['/api/trips']['post']['requestBody']['content']['application/json']

export type GetTripResponse =
  paths['/api/trips/{tripId}']['get']['responses']['200']['content']['application/json']

export type TripRole = GetTripResponse['result']['role']

export type GetTripMetadataResponse =
  paths['/api/trips/{tripId}/metadata']['get']['responses']['200']['content']['application/json']

export type RefreshTripResponse =
  paths['/api/trips/{tripId}/refresh']['post']['responses']['200']['content']['application/json']

export type RecordTripViewResponse =
  paths['/api/trips/{tripId}/record-view']['post']['responses']['200']['content']['application/json']

export type DeleteTripResponse =
  paths['/api/trips/{tripId}']['delete']['responses']['200']['content']['application/json']

export type UpdateTripRequest =
  paths['/api/trips/{tripId}']['patch']['requestBody']['content']['application/json']

export type UpdateTripResponse =
  paths['/api/trips/{tripId}']['patch']['responses']['200']['content']['application/json']

export type UploadTripImageRequest =
  paths['/api/trips/{tripId}/trip-image']['post']['requestBody']['content']['application/json']

export type UploadTripImageResponse =
  paths['/api/trips/{tripId}/trip-image']['post']['responses']['200']['content']['application/json']

export type SendInviteRequest =
  paths['/api/trips/{tripId}/invitation']['post']['requestBody']['content']['application/json']

export type SendInviteResponse =
  paths['/api/trips/{tripId}/invitation']['post']['responses']['200']['content']['application/json']

export type AcceptInviteResponse =
  paths['/api/trips/{tripId}/invitations/{invitationId}/accept']['post']['responses']['200']['content']['application/json']

export type EditInviteRequest =
  paths['/api/trips/{tripId}/invitations/{invitationId}']['patch']['requestBody']['content']['application/json']

export type EditInviteResponse =
  paths['/api/trips/{tripId}/invitations/{invitationId}']['patch']['responses']['200']['content']['application/json']

export type EditInviteError =
  paths['/api/trips/{tripId}/invitations/{invitationId}']['patch']['responses']['404']['content']['application/json']

export type DeleteInviteResponse =
  paths['/api/trips/{tripId}/invitations/{invitationId}']['delete']['responses']['200']['content']['application/json']

export type EditCollaboratorRequest =
  paths['/api/trips/{tripId}/collaborators/{userId}']['patch']['requestBody']['content']['application/json']

export type EditCollaboratorResponse =
  paths['/api/trips/{tripId}/collaborators/{userId}']['patch']['responses']['200']['content']['application/json']

export type EditCollaboratorError =
  paths['/api/trips/{tripId}/collaborators/{userId}']['patch']['responses']['404']['content']['application/json']

export type RemoveCollaboratorResponse =
  paths['/api/trips/{tripId}/collaborators/{userId}']['delete']['responses']['200']['content']['application/json']

export type GetTripError =
  paths['/api/trips/{tripId}']['get']['responses']['404']['content']['application/json']

export type GetTripCollaboratorError =
  | paths['/api/trips/{tripId}/collaborators']['get']['responses']['404']['content']['application/json']

export type GetTripMetadataError =
  paths['/api/trips/{tripId}/metadata']['get']['responses']['404']['content']['application/json']

export type RefreshTripError =
  paths['/api/trips/{tripId}/refresh']['post']['responses']['404']['content']['application/json']

export type RecordTripViewError =
  paths['/api/trips/{tripId}/record-view']['post']['responses']['404']['content']['application/json']

export type CreateTripError =
  paths['/api/trips']['post']['responses']['422']['content']['application/json']

export type UpdateTripError =
  | paths['/api/trips/{tripId}']['put']['responses']['404']['content']['application/json']
  | paths['/api/trips/{tripId}']['put']['responses']['422']['content']['application/json']

export type DeleteTripError =
  paths['/api/trips/{tripId}']['delete']['responses']['404']['content']['application/json']

export type UploadTripImageError =
  | paths['/api/trips/{tripId}/trip-image']['post']['responses']['403']['content']['application/json']
  | paths['/api/trips/{tripId}/trip-image']['post']['responses']['404']['content']['application/json']
  | paths['/api/trips/{tripId}/trip-image']['post']['responses']['422']['content']['application/json']

export type SendInviteError =
  | paths['/api/trips/{tripId}/invitation']['post']['responses']['404']['content']['application/json']

export type AcceptInviteError =
  | paths['/api/trips/{tripId}/invitations/{invitationId}/accept']['post']['responses']['404']['content']['application/json']
  | paths['/api/trips/{tripId}/invitations/{invitationId}/accept']['post']['responses']['422']['content']['application/json']
  | paths['/api/trips/{tripId}/invitations/{invitationId}/accept']['post']['responses']['500']['content']['application/json']
export type RevokeInviteError =
  | paths['/api/trips/{tripId}/invitations/{invitationId}']['delete']['responses']['404']['content']['application/json']
export type RemoveCollaboratorRequest =
  paths['/api/trips/{tripId}/invitation']['post']['requestBody']['content']['application/json']

export type RemoveCollaboratorError =
  | paths['/api/trips/{tripId}/collaborators/{userId}']['delete']['responses']['404']['content']['application/json']
  | paths['/api/trips/{tripId}/collaborators/{userId}']['delete']['responses']['403']['content']['application/json']

export type GetFlightScheduleError =
  paths['/api/trips/misc/flight-schedule']['get']['responses']['502']['content']['application/json']

export type GetLowStockTripItemsResponse =
  paths['/api/trips/{tripId}/low-stock-items']['get']['responses']['200']['content']['application/json']

export type Place = definitions['place']

export type GenerateTripAiResponse =
  paths['/api/trips/generate-trip']['post']['responses']['200']['content']['application/json']

export type GenerateTripAiWithDestinationRequest =
  paths['/api/trips/v2/generate-trip']['post']['requestBody']['content']['application/json']

export type GenerateMultiDestinationTripRequest =
  paths['/api/trips/v2/generate-trip']['post']['requestBody']['content']['application/json']

export type GenerateTripThemesResponse =
  paths['/api/trips/generate-themes']['post']['responses']['200']['content']['application/json']

export type GenerateTripThemesRequest =
  paths['/api/trips/generate-themes']['post']['requestBody']['content']['application/json']

export type ChatListResponse =
  paths['/api/trips/v2/chats']['get']['responses']['200']['content']['application/json']['result']

export type TripPlannerChat = ChatListResponse[number]

export type TripPlannerChatDetails =
  paths['/api/trips/v2/chats/{chatId}']['get']['responses']['200']['content']['application/json']['result']
  // TODO(TRP-4090): drop this local augmentation once @luxuryescapes/contract-trip is bumped
  // to >=0.10.74 (which types `hiddenFromCustomer` on the `chatWithMessages` schema natively).
  // The backend already returns it at runtime; typing it locally lets us consume the field
  // without pulling in the unrelated 0.10.74 assistant-surface (snapshotRef) contract migration.
  & { hiddenFromCustomer?: boolean }

export type TripPlannerChatMessage = TripPlannerChatDetails['messages'][number]

export type ContractMessagePart = TripPlannerChatMessage['parts'][number]

export type ContractUIComponentData = Extract<ContractMessagePart, { type: 'ui_component' }>['data']

export const STREAM_EVENT = {
  MESSAGE_START: 'message_start',
  PARTS_UPDATE: 'parts_update',
  MESSAGE_END: 'message_end',
  UI_PART_STARTED: TRIP_CHAT_STREAM_EVENT.UI_PART_STARTED,
  UI_PART_PATCH: TRIP_CHAT_STREAM_EVENT.UI_PART_PATCH,
  UI_PART_COMPLETED: TRIP_CHAT_STREAM_EVENT.UI_PART_COMPLETED,
  UI_PART_FAILED: TRIP_CHAT_STREAM_EVENT.UI_PART_FAILED,
  ITINERARY_UPDATE_STARTED: 'itinerary_update_started',
  ITINERARY_UPDATES: 'itinerary_updates',
  CONTENT_COMPLETE: 'content_complete',
  ENRICHMENT_COMPLETE: 'enrichment_complete',
  CHAT_LOADING_STATE: 'chat_loading_state',
  CHAT_METADATA_UPDATED: 'chat_metadata_updated',
  HOTEL_SEARCH_RESULTS: 'hotel_search_results',
  EXPERIENCE_SEARCH_RESULTS: 'experience_search_results',
  HOTEL_SEARCH_ERROR: 'hotel_search_error',
  EXPERIENCE_SEARCH_ERROR: 'experience_search_error',
  CHAT_UI_ACTION: 'chat_ui_action',
  STATUS: 'status',
  HEARTBEAT: 'heartbeat',
  TOKEN_BATCH: 'token_batch',
} as const

export type StreamEventType = typeof STREAM_EVENT[keyof typeof STREAM_EVENT]

export type ChatToolLoadingPhase = 'start' | 'complete'

export interface ChatToolLoadingStateData {
  kind: 'tool_call'
  phase: ChatToolLoadingPhase
  key: string
  callId: string
}

export interface ChatMetadataUpdatedData {
  chatId: number
  name: string
  updatedAt: string
}

export type ContractCardsListData = TripChatCardsListUiComponentData

export type ContractPillsListData = TripChatPillsListUiComponentData

export type ContractTestimonialData = TripChatTestimonialUiComponentData

export type StreamedCardsListData = TripChatCardsListUiComponentData

export type StreamedPillsListData = TripChatPillsListUiComponentData

export type StreamedTestimonialData = TripChatTestimonialUiComponentData

export type StreamedCarouselData = TripChatCarouselUiComponentData

export type StreamedProgressiveUiData =
  | Exclude<TripChatProgressiveUiComponentData, { type: 'carousel' }>
  | StreamedCarouselData

export type ProgressiveUiPatchOp = TripChatProgressiveUiPatchOp

export interface ProgressiveUiPartStartedEvent {
  event: typeof STREAM_EVENT.UI_PART_STARTED
  messageId: string
  partId: string
  data: StreamedProgressiveUiData
}

export type ProgressiveUiPartPatchEvent = TripChatUiPartPatchEvent

export interface ProgressiveUiPartCompletedEvent {
  event: typeof STREAM_EVENT.UI_PART_COMPLETED
  messageId: string
  partId: string
  data: StreamedProgressiveUiData
}

export type ProgressiveUiPartFailedEvent = TripChatUiPartFailedEvent

export type ProgressiveUiPartEvent =
  | ProgressiveUiPartStartedEvent
  | ProgressiveUiPartPatchEvent
  | ProgressiveUiPartCompletedEvent
  | ProgressiveUiPartFailedEvent

export type StreamedCardsListOption = StreamedCardsListData['options'][number]

export type StreamedPillsListOption = StreamedPillsListData['options'][number]

export type StreamedTestimonialReview = StreamedTestimonialData['reviews'][number]

export type StreamedCarouselOfferId = StreamedCarouselData['offerIds'][number]

export type StreamedUiPartStatus = 'loading' | 'streaming' | 'complete'

type StreamedUiPartMetadata = {
  streamedPartId: string
  streamingStatus: StreamedUiPartStatus
}

export type StreamedCardsListUiComponentData = StreamedCardsListData & StreamedUiPartMetadata

export type StreamedPillsListUiComponentData = StreamedPillsListData & StreamedUiPartMetadata

export type StreamedTestimonialUiComponentData = StreamedTestimonialData & StreamedUiPartMetadata

export type StreamedCarouselUiComponentData = StreamedCarouselData & StreamedUiPartMetadata

export type StreamedProgressiveUiComponentData = StreamedProgressiveUiData & StreamedUiPartMetadata

/** Hotel and experience search offer shape from stream events. */
export interface HotelSearchOffer {
  id: string
  name: string
  coordinates?: { lat: number; lng: number }
}

/**
 * Data shape passed when the user clicks "Map view" on a hotel carousel
 * or when hotel search results are used to populate the map.
 */
export interface HotelMapViewData {
  searchQuery: string
  offers: Array<HotelSearchOffer>
}

/**
 * Data shape passed when the user clicks "Map view" on an experience carousel.
 * Shares the same offer shape as hotels since both carry { id, name, coordinates }.
 */
export interface ExperienceMapViewData {
  searchQuery: string
  offers: Array<HotelSearchOffer>
}

export interface OfferMapSuggestionData {
  offerId: string
  name: string
  coordinates: { lat: number; lng: number }
  offerType: 'hotel' | 'experience'
}

export interface PlaceMapSuggestionData {
  googlePlaceId: string
  name: string
  coordinates: { lat: number; lng: number }
}

/**
 * Hotel search results stream event data (SSE payload).
 * Not covered by the REST contract — hand-defined until svc-trip
 * publishes stream event schemas.
 */
export interface HotelSearchResultsData {
  searchQuery: string
  offers: Array<HotelSearchOffer>
  /** OpenAI tool call id used for correlation. */
  hotelSearchCallId?: string
  /** Travel dates used for the hotel search (specific, flexible, or anytime). */
  searchDates?: TypeaheadTravelDates
}

export interface ExperienceSearchResultsData {
  searchQuery: string
  offers: Array<HotelSearchOffer>
  /** OpenAI tool call id used for correlation. */
  searchCallId?: string
}

/**
 * Hotel search error stream event data (SSE payload).
 * Not covered by the REST contract — hand-defined until svc-trip
 * publishes stream event schemas.
 */
export interface HotelSearchErrorData {
  message: string
  searchQuery?: string
  /** OpenAI tool call id used for correlation. */
  hotelSearchCallId?: string
}

export interface ExperienceSearchErrorData {
  message: string
  searchQuery?: string
  /** OpenAI tool call id used for correlation. */
  searchCallId?: string
}

export interface ChatUiActionData {
  action: 'set_right_panel_view'
  view: 'map' | 'itinerary'
  lat?: number
  lng?: number
  zoom?: number
}

/** Camera position derived from ChatUiActionData with all coordinates required. */
export type MapCameraPosition = Required<Pick<ChatUiActionData, 'lat' | 'lng' | 'zoom'>>

// Status event data — typed phases for loading indicator copy
export type StatusReasoningData = {
  phase: 'reasoning'
  summaries?: Array<string>
}

export type StatusToolCallingData = {
  phase: 'tool_calling'
  tool: string
  args: Record<string, unknown>
}

export type StatusEventData = StatusReasoningData | StatusToolCallingData

/** Tool names from status/tool_calling events. Used for label mapping and filtering. */
export const TOOL_NAME = {
  SEARCH_HOTELS: 'search_hotels',
  GET_SIMILAR_OFFERS: 'get_similar_offers',
  GET_FLASH_LEAD_PRICES: 'get_flash_lead_prices',
  SEARCH_EXPERIENCES: 'search_experiences',
  SEARCH_CRUISES: 'search_cruises',
  SEARCH_AIRPORT_TRANSFERS: 'search_airport_transfers',
  SEARCH_AIRPORT_LOUNGE: 'search_airport_lounge',
  SEARCH_OFFER_REVIEWS: 'search_offer_reviews',
  SEARCH_FLIGHT_SALES: 'search_flight_sales',
  SEARCH_FLIGHTS: 'search_flights',
  SEARCH_FLIGHT_CALENDAR_GRID: 'search_flight_calendar_grid',
  GET_ORDERS: 'get_orders',
  GET_ORDER_DETAILS: 'get_order_details',
  GET_ITINERARY: 'get_itinerary',
  UPDATE_ITINERARY: 'update_itinerary',
  GET_CUSTOMER_SHOPPING_CARTS: 'get_customer_shopping_carts',
  GET_CUSTOMER_WALLET: 'get_customer_wallet',
  GET_COMMUNICATIONS_PREFERENCES: 'get_communications_preferences',
  UPDATE_COMMUNICATIONS_PREFERENCES: 'update_communications_preferences',
  GET_RECENT_CUSTOMER_EMAILS: 'get_recent_customer_emails',
  LOOKUP_OFFER: 'lookup_offer',
  GET_OFFER_REVIEWS: 'get_offer_reviews',
  RESEARCH_SUPPORT_ARTICLES: 'research_support_articles',
  GET_WEATHER_FORECAST: 'get_weather_forecast',
  GET_CURRENCY_EXCHANGE_RATES: 'get_currency_exchange_rates',
  LOOKUP_GOOGLE_PLACES: 'lookup_google_places',
  LOOKUP_OFFER_GENERAL_AVAILABILITY: 'lookup_offer_general_availability',
  LOOKUP_OFFER_SPECIFIC_AVAILABILITY: 'lookup_offer_specific_availability',
  GET_CHAT_NAME: 'get_chat_name',
  RENAME_CHAT: 'rename_chat',
  SUBMIT_WEDDING_INQUIRY: 'submit_wedding_inquiry',
  SET_TRIP_PLANNER_VIEW: 'set_trip_planner_view',
} as const

// Enrichment status from contract — used by derived enrichment state in useItineraryUpdates
export type EnrichmentStatus = NonNullable<definitions['tripChatItem']['enrichmentStatus']>

// Re-export itinerary types for convenience
export type TripStopWithDays = definitions['tripStopWithDays']
export type TripDayWithItems = definitions['tripDayWithItems']
export type TripChatItem = definitions['tripChatItem']
export type TripChatItemCartStatus = NonNullable<TripChatItem['cartStatus']>
export type TripChatFlightCartStatus = TripChatItemCartStatus
export type PlaceDetail = definitions['placeDetail']

// Itinerary data types - indexed from GetItineraryResponse
export type TripItineraryData = GetItineraryResponse['result']
export type TripStop = TripItineraryData['stops'][number]
export type TripDay = TripStop['days'][number]
export type TripItineraryItem = TripDay['items'][number]
export type TripItemWithoutDay = TripStop['itemsWithoutDay'][number]

type ContractTransportConnection = NonNullable<TripStop['transports']>[number]
type ContractTransportOption = ContractTransportConnection['options'][number]
type ContractTransportOptionSavedItemData = NonNullable<ContractTransportOption['savedItemData']>

type TransportOptionSavedItemDataExtension = {
  // TODO: remove once contract-trip exposes default transport identity.
  isDefaultTransport?: boolean
}
type TransportOptionSavedItemData =
  ContractTransportOptionSavedItemData extends object
    ? ContractTransportOptionSavedItemData & TransportOptionSavedItemDataExtension
    : ContractTransportOptionSavedItemData

type TransportConnectionExtension = {
  // TODO: remove once contract-trip exposes default flight search params.
  flightSearchDefaults?: {
    originAirportCode?: string
    originAirportName?: string
    destinationAirportCode?: string
    destinationAirportName?: string
    departureDate?: string
  }
}

export type TransportOption = Omit<ContractTransportOption, 'savedItemData' | 'returnLeg'> & {
  savedItemData?: TransportOptionSavedItemData
  returnLeg?: TransportOption
}
export type TransportConnection = Omit<ContractTransportConnection, 'options'> & TransportConnectionExtension & {
  options: Array<TransportOption>
}

/** savedItemData narrowed to the le_flight source variant. */
export type LeFlightSavedItemData =
  Extract<ContractTransportOptionSavedItemData, { source: 'le_flight' }>
  & TransportOptionSavedItemDataExtension

/** Type guard that narrows TransportOption.savedItemData to the le_flight shape. */
export function isLeFlightSavedItemData(data: TransportOption['savedItemData']): data is LeFlightSavedItemData {
  return data?.source === 'le_flight'
}

/** savedItemData narrowed to the le_cruise source variant. */
export type LeCruiseSavedItemData =
  Extract<ContractTransportOptionSavedItemData, { source: 'le_cruise' }>
  & TransportOptionSavedItemDataExtension

/** Type guard that narrows TransportOption.savedItemData to the le_cruise shape. */
export function isLeCruiseSavedItemData(data: TransportOption['savedItemData']): data is LeCruiseSavedItemData {
  return data?.source === 'le_cruise'
}

/** savedItemData narrowed to the chat_flight source variant. */
export type ChatFlightSavedItemData =
  Extract<ContractTransportOptionSavedItemData, { source: 'chat_flight' }>
  & TransportOptionSavedItemDataExtension

/** Type guard that narrows TransportOption.savedItemData to the chat_flight shape. */
export function isChatFlightSavedItemData(data: TransportOption['savedItemData']): data is ChatFlightSavedItemData {
  return data?.source === 'chat_flight'
}

/** savedItemData narrowed to the custom_flight source variant (user-added via chat). */
export type CustomFlightSavedItemData =
  Extract<ContractTransportOptionSavedItemData, { source: 'custom_flight' }>
  & TransportOptionSavedItemDataExtension

/** Type guard that narrows TransportOption.savedItemData to the custom_flight shape. */
export function isCustomFlightSavedItemData(data: TransportOption['savedItemData']): data is CustomFlightSavedItemData {
  return data?.source === 'custom_flight'
}

/** Matches any saved flight data source used by TripChat. */
export type FlightSavedItemData =
  | LeFlightSavedItemData
  | ChatFlightSavedItemData
  | CustomFlightSavedItemData

/** Type guard that matches any saved flight data source used by TripChat. */
export function isFlightSavedItemData(data: TransportOption['savedItemData']): data is FlightSavedItemData {
  return isLeFlightSavedItemData(data) || isChatFlightSavedItemData(data) || isCustomFlightSavedItemData(data)
}

/** Matches either le_flight or custom_flight saved data for shared rendering logic. */
export type FlightCardSavedItemData = LeFlightSavedItemData | CustomFlightSavedItemData

/** Matches flight sources that can appear in the TripChat cart panel. */
export type CartableFlightSavedItemData = LeFlightSavedItemData | ChatFlightSavedItemData

/** Type guard that matches flight sources that can appear in the TripChat cart panel. */
export function isCartableFlightSavedItemData(data: TransportOption['savedItemData']): data is CartableFlightSavedItemData {
  return isLeFlightSavedItemData(data) || isChatFlightSavedItemData(data)
}

/** Type guard that matches any flight source that should render as a rich tile card. */
export function isFlightCardSavedItemData(data: TransportOption['savedItemData']): data is FlightCardSavedItemData {
  return data?.source === 'le_flight' || data?.source === 'custom_flight'
}

export type StopVisitRole = definitions['stopVisit']['role']

export const STOP_VISIT_ROLE: Record<StopVisitRole, StopVisitRole> = {
  ACTIVITY: 'ACTIVITY',
  STAY: 'STAY',
  DEPARTURE: 'DEPARTURE',
  ARRIVAL: 'ARRIVAL',
  VIA: 'VIA',
} as const

export interface StreamingStopVisit extends Pick<definitions['stopVisit'], 'tripStopId' | 'startDayId' | 'endDayId' | 'role'> {
  startDayNumber?: number
  endDayNumber?: number
}

/**
 * ItineraryUpdate includes full entity data for immediate rendering.
 * The frontend can add these entities to the itinerary immediately,
 * then update them when enrichment_complete events arrive.
 *
 * Item events carry `stopVisits` for multi-stop/multi-day placement.
 * Legacy `stopId`/`dayId` are still sent by the backend for backward compat
 * but should not be used — prefer `stopVisits`.
 */
export type ItineraryUpdate =
  | { kind: 'stop_added'; stopId: number; stop: TripStopWithDays }
  | { kind: 'stop_updated'; stopId: number; stop: TripStopWithDays }
  | { kind: 'stop_deleted'; stopId: number }
  | { kind: 'day_added'; dayId: number; stopId: number; day: TripDayWithItems }
  | { kind: 'day_deleted'; dayId: number; stopId: number }
  | { kind: 'item_added'; itemId: number; stopVisits: Array<StreamingStopVisit>; item: TripChatItem; /** @deprecated use stopVisits */ stopId?: number; /** @deprecated use stopVisits */ dayId?: number }
  | { kind: 'item_updated'; itemId: number; stopVisits: Array<StreamingStopVisit>; item: TripChatItem; /** @deprecated use stopVisits */ stopId?: number; /** @deprecated use stopVisits */ dayId?: number }
  | { kind: 'item_deleted'; itemId: number; stopVisits: Array<StreamingStopVisit>; /** @deprecated use stopVisits */ stopId?: number; /** @deprecated use stopVisits */ dayId?: number }

export type EnrichmentCompleteEvent = TripChatEnrichmentCompleteEvent

export type GenerateChatTitleResponse =
  paths['/api/trips/v2/chats/{chatId}/title']['post']['responses']['200']['content']['application/json']['result']

export type GenerateChatTitleRequest =
  paths['/api/trips/v2/chats/{chatId}/title']['post']['requestBody']['content']['application/json']

export type GenerateChatTitleParams = GenerateChatTitleRequest & {
  chatId: number
}

export type GenerateSalesAgentSummaryResponse =
  paths['/api/trips/v2/chats/{chatId}/sales-summary']['post']['responses']['200']['content']['application/json']['result']

export type CreateChatRequest =
  paths['/api/trips/v2/chats']['post']['requestBody']['content']['application/json']

export type UpdateChatRequest =
  paths['/api/trips/v2/chats/{chatId}']['patch']['requestBody']['content']['application/json']

export type UpdateChatResponse =
  paths['/api/trips/v2/chats/{chatId}']['patch']['responses']['200']['content']['application/json']['result']

export type ManualItineraryDeleteRequest =
  paths['/api/trips/v2/chats/{chatId}/manual-itinerary-delete']['post']['requestBody']['content']['application/json']

export type ManualItineraryDeleteResponse =
  paths['/api/trips/v2/chats/{chatId}/manual-itinerary-delete']['post']['responses']['200']['content']['application/json']['result']

type GeneratedManualItineraryMoveRequest =
  paths['/api/trips/v2/chats/{chatId}/manual-itinerary-move']['post']['requestBody']['content']['application/json']

export type ManualItineraryMovePlacementIntent =
  | { placementType: 'before'; itemId: number }
  | { placementType: 'after'; itemId: number }
  | { placementType: 'start' }
  | { placementType: 'end' }

export type ManualItineraryMoveRequest = Omit<GeneratedManualItineraryMoveRequest, 'moves'> & {
  moves: Array<GeneratedManualItineraryMoveRequest['moves'][number] & {
    placement?: ManualItineraryMovePlacementIntent
  }>
}

export type ManualItineraryMoveResponse =
  paths['/api/trips/v2/chats/{chatId}/manual-itinerary-move']['post']['responses']['200']['content']['application/json']['result']

export type CreateMessageRequest =
  paths['/api/trips/v3/messages']['post']['requestBody']['content']['application/json']

export type ChatMessageAttachment = NonNullable<CreateMessageRequest['files']>[number]

export type CreateMessageResponse =
  paths['/api/trips/v3/messages']['post']['responses']['202']['content']['application/json']['result']

export type SubmitMessageFeedbackRequest =
  paths['/api/trips/v2/chats/{chatId}/messages/{messageId}/feedback']['post']['requestBody']['content']['application/json']

export type MessageFeedbackReaction = SubmitMessageFeedbackRequest['reaction']

export type SubmitMessageFeedbackResponse =
  paths['/api/trips/v2/chats/{chatId}/messages/{messageId}/feedback']['post']['responses']['200']['content']['application/json']['result']

export type GenerateTripThemesV2Response =
  paths['/api/trips/v2/generate-themes']['post']['responses']['200']['content']['application/json']

export type GenerateTripThemesV2Request =
  paths['/api/trips/v2/generate-themes']['post']['requestBody']['content']['application/json']

export type RegenerateDayAiResponse =
  paths['/api/trips/regenerate-day']['post']['responses']['200']['content']['application/json']

export type RegenerateDayAiRequest =
  paths['/api/trips/regenerate-day']['post']['requestBody']['content']['application/json']

export type GenerateMustDoActivitiesResponse =
  paths['/api/trips/generate-must-do-activities']['post']['responses']['200']['content']['application/json']

export type GenerateMustDoActivitiesRequest =
  paths['/api/trips/generate-must-do-activities']['post']['requestBody']['content']['application/json']

export type UpdateTravelPreferencesResponse =
  paths['/api/trips/travel-preferences']['post']['responses']['200']['content']['application/json']

export type UpdateTravelPreferencesRequest =
  paths['/api/trips/travel-preferences']['post']['requestBody']['content']['application/json']

export type UndoRegenerateDayAiResponse =
  paths['/api/trips/undo-regenerate-day']['post']['responses']['200']['content']['application/json']

export type UndoRegenerateDayAiRequest =
  paths['/api/trips/undo-regenerate-day']['post']['requestBody']['content']['application/json']

export type RegenerateItemAiResponse =
  paths['/api/trips/{tripId}/generate-alternative-item']['post']['responses']['200']['content']['application/json']

export type RegenerateItemAiRequest =
  paths['/api/trips/{tripId}/generate-alternative-item']['post']['requestBody']['content']['application/json']

export type RegenerateItemAiError =
  | paths['/api/trips/{tripId}/generate-alternative-item']['post']['responses']['400']['content']['application/json']
  | paths['/api/trips/{tripId}/generate-alternative-item']['post']['responses']['404']['content']['application/json']
  | paths['/api/trips/{tripId}/generate-alternative-item']['post']['responses']['500']['content']['application/json']

export type AddBasicActivityToTripResponse =
  paths['/api/trips/{tripId}/basic-activity']['post']['responses']['200']['content']['application/json']

export type AddBasicActivityToTripRequest =
  paths['/api/trips/{tripId}/basic-activity']['post']['requestBody']['content']['application/json']

export type GenerateSuggestedLocationsResponse =
  paths['/api/trips/generate-suggested-locations']['post']['responses']['200']['content']['application/json']

export type GenerateSuggestedLocationsRequest =
  paths['/api/trips/generate-suggested-locations']['post']['requestBody']['content']['application/json']

export type AddBasicActivityToTripError =
  | paths['/api/trips/{tripId}/basic-activity']['post']['responses']['400']['content']['application/json']
  | paths['/api/trips/{tripId}/basic-activity']['post']['responses']['403']['content']['application/json']
  | paths['/api/trips/{tripId}/basic-activity']['post']['responses']['404']['content']['application/json']
  | paths['/api/trips/{tripId}/basic-activity']['post']['responses']['500']['content']['application/json']

export type GetChatResponse = TripPlannerChatDetails

export interface DeleteChatResponse {
  success: boolean
}

export interface ClaimChatsRequest {
  visitorId: string
}

export interface ClaimChatsResponse {
  transferredChats: number
}

export type SwitchDefaultTransportResponse =
  paths['/api/trips/v2/items/{itemId}/default-transport']['patch']['responses']['200']['content']['application/json']['result']

export type UpdateTripChatItemRequest =
  paths['/api/trips/v2/items/{itemId}']['patch']['requestBody']['content']['application/json']

export type UpdateTripChatItemResponse =
  paths['/api/trips/v2/items/{itemId}']['patch']['responses']['200']['content']['application/json']['result']

export type GetItineraryResponse =
  paths['/api/trips/v2/chats/{chatId}/itinerary']['get']['responses']['200']['content']['application/json']

export type GetPlaceDetailResponse =
  paths['/api/trips/v2/places/{googlePlaceId}']['get']['responses']['200']['content']['application/json']

export type GetPlaceDetailResult = GetPlaceDetailResponse['result']

export type GetPlaceOverviewResponse =
  paths['/api/trips/v2/places/{googlePlaceId}/overview']['get']['responses']['200']['content']['application/json']

export type GetPlaceOverviewResult = GetPlaceOverviewResponse['result']

export type GetPlaceBestTimeToVisitResponse =
  paths['/api/trips/v2/places/{googlePlaceId}/best-time-to-visit']['get']['responses']['200']['content']['application/json']

export type GetPlaceBestTimeToVisitResult = GetPlaceBestTimeToVisitResponse['result']

export type GetPlaceWhatToLoveResponse =
  paths['/api/trips/v2/places/{googlePlaceId}/what-to-love']['get']['responses']['200']['content']['application/json']

export type GetPlaceWhatToLoveResult = GetPlaceWhatToLoveResponse['result']

export type GetPlaceAreasResponse =
  paths['/api/trips/v2/places/{googlePlaceId}/areas']['get']['responses']['200']['content']['application/json']

export type GetPlaceAreasResult = GetPlaceAreasResponse['result']

export type GetPlaceInsiderTipsResponse =
  paths['/api/trips/v2/places/{googlePlaceId}/insider-tips']['get']['responses']['200']['content']['application/json']

export type GetPlaceInsiderTipsResult = GetPlaceInsiderTipsResponse['result']

export type GetPlaceHighlightsResponse =
  paths['/api/trips/v2/places/{googlePlaceId}/highlights']['get']['responses']['200']['content']['application/json']

export type GetPlaceHighlightsResult = GetPlaceHighlightsResponse['result']

export type GetPlaceInfoResponse =
  paths['/api/trips/v2/places/{googlePlaceId}/info']['get']['responses']['200']['content']['application/json']

export type GetPlaceInfoResult = GetPlaceInfoResponse['result']

export type PlaceInfoImageMode =
  NonNullable<paths['/api/trips/v2/places/{googlePlaceId}/info']['get']['parameters']['query']['imageMode']>

export type PlaceOpeningHours = NonNullable<GetPlaceInfoResult['openingHours']>

export type PlaceInfoReview = NonNullable<GetPlaceInfoResult['reviews']>[number]

export type LocationDrawImagesRequest =
  paths['/api/trips/v2/location-draw-images']['post']['requestBody']['content']['application/json']

export type LocationDrawImagesResponse =
  paths['/api/trips/v2/location-draw-images']['post']['responses']['200']['content']['application/json']

export type LocationDrawImagesResult =
  paths['/api/trips/v2/location-draw-information']['post']['responses']['200']['content']['application/json']

export type LocationDrawInformationRequest =
  paths['/api/trips/v2/location-draw-information']['post']['requestBody']['content']['application/json']

export type PlaceDrawDetailsRequest =
  paths['/api/trips/v2/place-draw-details']['post']['requestBody']['content']['application/json']

export type PlaceDrawDetailsResponse =
  paths['/api/trips/v2/place-draw-details']['post']['responses']['200']['content']['application/json']

export type HotelSearchResult =
  paths['/api/trips/v2/search/hotels/page']['get']['responses']['200']['content']['application/json']['result']
export type HotelSearchResultItem = HotelSearchResult['items'][number]
export type HotelSearchSection = HotelSearchResult['sections'][number]

export type GetOfferPropertySurroundingsResponse =
  paths['/api/trips/v2/offers/{offerId}/surroundings']['get']['responses']['200']['content']['application/json']

export type HotelSearchSavedItemData = NonNullable<HotelSearchResultItem['savedItemData']>

export type OfferDetailResponse =
  paths['/api/trips/v2/offers/{offerId}']['get']['responses']['200']['content']['application/json']

export type OfferDetailResult = OfferDetailResponse['result']

type HotelRoomOptionsEndpoint =
  paths['/api/trips/v2/offers/{offerId}/room-options']['post']

export type HotelRoomOptionsRequest =
  HotelRoomOptionsEndpoint['requestBody']['content']['application/json']

export type HotelRoomOptionsResponse =
  HotelRoomOptionsEndpoint['responses']['200']['content']['application/json']

export type HotelRoomOptionsResult = HotelRoomOptionsResponse['result']

export type HotelRoomOptionSelection = HotelRoomOptionsRequest['selections'][number]

export type HotelRoomOptionInclusion =
  NonNullable<HotelRoomOptionsResult['roomOptions'][number]['rates'][number]['inclusions']>[number]

export type HotelRoomOptionsRate = HotelRoomOptionsResult['roomOptions'][number]['rates'][number]

export type OfferReviewsResponse =
  paths['/api/trips/v2/offers/{offerId}/reviews']['get']['responses']['200']['content']['application/json']

export type OfferReviewsResult = OfferReviewsResponse['result']

export type ExperienceDetailResponse =
  paths['/api/trips/v2/experience-offers/{offerId}']['get']['responses']['200']['content']['application/json']

export type ExperienceDetailResult = ExperienceDetailResponse['result']

export type ExperienceSearchResponse =
  paths['/api/trips/v2/search/experiences']['get']['responses']['200']['content']['application/json']

export type ExperienceSearchResultItem = ExperienceSearchResponse['result'][number]

export type ExperienceSearchContext = NonNullable<ExperienceSearchResponse['searchContext']>

export interface ExperienceSearchResult {
  experiences: ExperienceSearchResponse['result']
  searchContext: ExperienceSearchResponse['searchContext']
}

export interface ExperienceSearchFallbackPlace {
  placeId: string
  name: string
  type?: string
}
